import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Layers, Target, CheckCircle2, Award, ShieldCheck, CreditCard } from "lucide-react";
import PublicShell from "@/components/public/PublicShell";
import LeadDialog from "@/components/public/LeadDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

export default function ProgramDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        api.get(`/programs/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    const payNow = async () => {
        if (!user) { toast.info("Please log in to enrol."); navigate("/login"); return; }
        setPaying(true);
        try {
            const { data } = await api.post("/payments/checkout", { program_slug: slug, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(apiError(e)); setPaying(false); }
    };

    if (notFound)
        return <PublicShell><div className="container-page section-pad text-center"><h1 className="font-heading text-2xl font-bold">Program not found</h1><Button asChild className="mt-6 rounded-full"><Link to="/programs">Back to Programs</Link></Button></div></PublicShell>;
    if (!p)
        return <PublicShell><div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></PublicShell>;

    return (
        <PublicShell>
            <section className="bg-secondary text-secondary-foreground">
                <div className="container-page py-14">
                    <Link to="/programs" className="inline-flex items-center text-sm text-secondary-foreground/60 hover:text-primary mb-6"><ArrowLeft className="mr-1 h-4 w-4" /> All Programs</Link>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8">
                            <Badge className="rounded-full bg-accent/20 text-accent border-0">{p.badge}</Badge>
                            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{p.title}</h1>
                            <p className="mt-4 text-lg text-secondary-foreground/70 max-w-2xl leading-relaxed">{p.description}</p>
                            <div className="mt-6 flex flex-wrap gap-4 text-sm">
                                <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {p.duration}</span>
                                <span className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> {p.level}</span>
                                <span className="flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Ages {p.age_group}</span>
                            </div>
                        </div>
                        <div className="lg:col-span-4">
                            <Card className="p-6 bg-card text-card-foreground">
                                <p className="font-heading text-4xl font-extrabold">{p.price}</p>
                                <p className="mt-1 text-sm text-foreground/50">One-time · Lifetime credential</p>
                                <Button size="lg" className="mt-5 w-full rounded-full" data-testid="pay-enroll-btn" disabled={paying} onClick={payNow}>
                                    {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-2 h-4 w-4" /> Enroll & Pay</>}
                                </Button>
                                <LeadDialog program={p.title} source="program_inquiry" title={`Enquire about ${p.title}`}
                                    trigger={<Button size="lg" variant="outline" className="mt-2 w-full rounded-full" data-testid="enroll-btn">Talk to a counselor</Button>} />
                                <ul className="mt-5 space-y-2 text-sm text-foreground/70">
                                    <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /> Mentor-reviewed projects</li>
                                    <li className="flex items-center gap-2"><Award className="h-4 w-4 text-success" /> Verified Digital Passport</li>
                                </ul>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
            <section className="section-pad">
                <div className="container-page grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Skills you'll build</h2>
                        <div className="mt-5 flex flex-wrap gap-2">
                            {(p.skills || []).map((s) => <span key={s} className="rounded-full bg-primary/10 text-primary px-4 py-2 text-sm font-medium">{s}</span>)}
                        </div>
                    </div>
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Outcomes</h2>
                        <ul className="mt-5 space-y-3">
                            {(p.outcomes || []).map((o) => <li key={o} className="flex items-start gap-3 text-foreground/80"><CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" /> {o}</li>)}
                        </ul>
                    </div>
                </div>
            </section>
        </PublicShell>
    );
}
